import React, { useState, useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  Typography,
  Box,
  CircularProgress,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Button,
  ButtonBase,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Clear as ClearIcon, Delete, FilterList as FilterListIcon, Visibility } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import * as api from '../../services/api';
import { runGalleryYearPurgeOnce } from '../../utils/runGalleryYearPurge';
import { rewriteToCdn } from '../../utils/cdn';
import { getResultGroupCoverUrl } from '../../utils/resultGalleryMedia';
import { userCardEnterSx } from '../shared/UserPageShell';
import DeviceFilterSelect from '../shared/DeviceFilterSelect';
import { ADMIN_GALLERY_FILTER_STICKY_TOP } from '../shared/MPhotoSectionTabs';
import { filterMachinesByAppProduct } from '../../utils/deviceMgmtFeatures';
import { useI18n } from '../../i18n/useI18n';

interface AdminPhotosProps {
  userId: string;
}

const PRO_ACCENT = '#e86a8a';

const toIso = (datetimeLocal: string): string => {
  if (!datetimeLocal) return '';
  const localDate = new Date(datetimeLocal);
  return Number.isNaN(localDate.getTime()) ? '' : localDate.toISOString();
};

const AdminPhotos: React.FC<AdminPhotosProps> = ({ userId }) => {
  const { pages: t } = useI18n();
  const [photoGroups, setPhotoGroups] = useState<api.PhotoGroupResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [machineCodeFilter, setMachineCodeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateOpen, setDateOpen] = useState(false);
  const [userMachines, setUserMachines] = useState<api.MachineInfo[]>([]);
  const limit = 10;
  const proMachines = React.useMemo(
    () => filterMachinesByAppProduct(userMachines, 'pro'),
    [userMachines]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getAdminUserMachines(userId);
        if (!cancelled) setUserMachines(res.machines || []);
      } catch (error) {
        console.error('Failed to load machines for admin photo filter:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    setPage(1);
    setPhotoGroups([]);
    setHasMore(true);
    fetchUserPhotos(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, machineCodeFilter, startDate, endDate]);

  const fetchUserPhotos = async (pageNumber: number = 1, resetData: boolean = false) => {
    try {
      setLoading(true);
      const startIso = toIso(startDate);
      const endIso = toIso(endDate);
      const response = await api.getUserPhotos(userId, pageNumber, limit, {
        ...(machineCodeFilter ? { machineCode: machineCodeFilter } : {}),
        ...(startIso ? { startDate: startIso } : {}),
        ...(endIso ? { endDate: endIso } : {}),
      });

      if (resetData) {
        setPhotoGroups(response.data);
      } else {
        setPhotoGroups((prev) => [...prev, ...response.data]);
      }

      setHasMore(pageNumber < response.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching user photos:', error);
      toast.error('Failed to load user photos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPhotosRef = useRef(fetchUserPhotos);
  fetchUserPhotosRef.current = fetchUserPhotos;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const count = await runGalleryYearPurgeOnce(
          `admin:pro:${userId}`,
          () => api.adminPurgeExpiredGalleries(userId, 'pro'),
          { toast: true }
        );
        if (!cancelled && count > 0) {
          setPage(1);
          void fetchUserPhotosRef.current(1, true);
        }
      } catch (err) {
        console.warn('purge old galleries', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchUserPhotos(nextPage, false);
    }
  };

  const handleDeleteClick = (photoId: string) => {
    setSelectedPhotoId(photoId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPhotoId) return;

    try {
      await api.adminDeleteUserPhoto(userId, selectedPhotoId);
      setPhotoGroups((prev) => prev.filter((group) => group.folderId !== selectedPhotoId));
      toast.success('Photo group deleted successfully');
    } catch (error) {
      console.error('Error deleting photo group:', error);
      toast.error('Failed to delete photo group');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedPhotoId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedPhotoId(null);
  };

  const handleOpenGallery = (group: api.PhotoGroupResponse) => {
    if (group.viewUrl) {
      window.open(group.viewUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    toast.error('Gallery link is not available for this group.');
  };

  const hasDateFilter = Boolean(startDate || endDate);
  const hasListFilter = Boolean(machineCodeFilter || hasDateFilter);

  const deviceFilter = (
    <Box
      sx={{
        mb: 1.5,
        position: { xs: 'sticky', md: 'static' },
        top: ADMIN_GALLERY_FILTER_STICKY_TOP,
        zIndex: (theme) => theme.zIndex.appBar - 3,
        bgcolor: 'background.default',
        pt: { xs: 0.5, md: 0 },
        pb: { xs: 1, md: 0 },
        mx: { xs: -0.5, md: 0 },
        px: { xs: 0.5, md: 0 },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
        <DeviceFilterSelect
          value={machineCodeFilter}
          onChange={setMachineCodeFilter}
          machines={proMachines}
          accentColor={PRO_ACCENT}
          fullWidth
          minWidth={0}
          sx={{ mb: 0, maxWidth: { xs: '100%', sm: 420 }, flex: '1 1 220px' }}
        />
        <ButtonBase
          onClick={() => setDateOpen((open) => !open)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.7,
            borderRadius: 2,
            border: '1px solid',
            borderColor: dateOpen || hasDateFilter ? alpha(PRO_ACCENT, 0.35) : alpha(PRO_ACCENT, 0.12),
            bgcolor: dateOpen || hasDateFilter ? alpha(PRO_ACCENT, 0.08) : alpha(PRO_ACCENT, 0.03),
            color: PRO_ACCENT,
          }}
        >
          <FilterListIcon sx={{ fontSize: 16 }} />
          <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
            {t.photos.filterByDate}
          </Typography>
          {hasDateFilter && (
            <Chip
              size="small"
              label="ON"
              sx={{
                height: 16,
                fontSize: '0.6rem',
                fontWeight: 800,
                bgcolor: PRO_ACCENT,
                color: '#fff',
                '& .MuiChip-label': { px: 0.6 },
              }}
            />
          )}
        </ButtonBase>
        {hasDateFilter && (
          <Tooltip title={t.photos.clearFilter}>
            <IconButton
              size="small"
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
              sx={{ color: PRO_ACCENT, bgcolor: alpha(PRO_ACCENT, 0.08) }}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Collapse in={dateOpen} timeout={220}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 1 }}>
          <TextField
            fullWidth
            type="datetime-local"
            label={t.common.fromDate}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth
            type="datetime-local"
            label={t.common.toDate}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </Collapse>
    </Box>
  );

  if (loading && page === 1) {
    return (
      <Box>
        {deviceFilter}
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (photoGroups.length === 0) {
    return (
      <Box>
        {deviceFilter}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={6}
          sx={{ backgroundColor: 'background.paper', borderRadius: 1 }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {hasListFilter ? 'No photos match this filter' : 'No Photos Found'}
          </Typography>
          <Typography color="text.secondary">
            {hasListFilter
              ? 'Không có gallery nào khớp bộ lọc'
              : "This user hasn't uploaded any photos yet"}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {deviceFilter}
      <InfiniteScroll
        dataLength={photoGroups.length}
        next={loadMore}
        hasMore={hasMore}
        loader={
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress />
          </Box>
        }
        scrollThreshold="200px"
        style={{ overflow: 'hidden' }}
        endMessage={
          <Typography textAlign="center" color="text.secondary" py={2}>
            {hasListFilter ? 'Đã tải hết gallery khớp bộ lọc' : 'No more photos to load'}
          </Typography>
        }
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: { xs: 1.25, sm: 2 },
            width: '100%',
            maxWidth: '100%',
          }}
        >
          {photoGroups.map((group, index) => {
            const coverUrl = getResultGroupCoverUrl(group.photos);
            return (
              <Card
                key={group.folderId}
                elevation={0}
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  ...userCardEnterSx(index),
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    pt: '75%',
                    width: '100%',
                    backgroundColor: '#f5f5f5',
                    overflow: 'hidden',
                  }}
                >
                  <CardMedia
                    component="img"
                    image={coverUrl ? rewriteToCdn(coverUrl) : '/placeholder.png'}
                    alt={`Photo group ${group.folderId}`}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                </Box>
                <CardContent sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 1.5 }, '&:last-child': { pb: { xs: 1, sm: 1.5 } } }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    title={group.folderId}
                    sx={{ fontSize: { xs: '0.68rem', sm: '0.8rem' }, fontFamily: 'ui-monospace, monospace', mb: 0.25 }}
                  >
                    {group.folderId}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.68rem', sm: '0.875rem' } }}
                  >
                    Created: {format(new Date(group.createdAt), 'dd/MM/yyyy')}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${group.photos.length} ảnh · ${group.printCount || 0} lượt in`}
                    sx={{
                      mt: 0.5,
                      height: 22,
                      fontWeight: 800,
                      fontSize: '0.68rem',
                      bgcolor: alpha('#fff', 0.92),
                      color: PRO_ACCENT,
                      border: `1px solid ${alpha(PRO_ACCENT, 0.35)}`,
                    }}
                  />
                </CardContent>
                <CardActions sx={{ px: { xs: 0.5, sm: 1 }, pt: 0, minHeight: 36 }}>
                  <Button
                    size="small"
                    startIcon={<Visibility sx={{ fontSize: { xs: 16, sm: 18 } }} />}
                    onClick={() => handleOpenGallery(group)}
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' }, minWidth: 0 }}
                  >
                    View
                  </Button>
                  <IconButton
                    onClick={() => handleDeleteClick(group.folderId)}
                    color="error"
                    size="small"
                    sx={{ ml: 'auto' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      </InfiniteScroll>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Photo Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this photo group? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminPhotos;
